export interface BrokerPartner {
  name: string;
  logo: string;
  tagline: string;
  note: string;
  url: string;
  badge: string;
  specs: { label: string; value: string }[];
}

export interface SelectionCriterion {
  label: string;
  text: string;
}

export interface BrokerReason {
  name: string;
  logo: string;
  role: string;
  reason: string;
}

/**
 * "Why these three" rationale shown inside the collapsed note on article pages.
 * Written as screening criteria, not as a ranking — the three brokers cover
 * different lanes, so the copy should never imply #1 / #2 / #3.
 */
export const brokerSelection = {
  title: "Why these three",
  lead:
    "Not a ranking and not interchangeable. Every broker offers market and limit orders; far fewer offer a usable stop-limit ticket or OCO. These three passed the same three screens and then split by what you actually need first.",
  criteria: [
    {
      label: "Order ticket",
      text: "Stop-limit and OCO have to sit on the same ticket as the plain stop — otherwise half of what this site explains is theoretical.",
    },
    {
      label: "Entry barrier",
      text: "A minimum deposit low enough to run a real position at real size, so the mechanics can be tested instead of imagined.",
    },
    {
      label: "Verifiable pricing",
      text: "A demo account that mirrors live pricing, so you can compare your fill against the execution costs described here.",
    },
  ] as SelectionCriterion[],
  reasons: [
    {
      name: "XM",
      logo: "/xm.jpg",
      role: "Starting point",
      reason:
        "Micro sizing makes a stop-limit test cost a few dollars instead of a few hundred — the cheapest way to see slippage happen on purpose.",
    },
    {
      name: "IC Markets",
      logo: "/ic.jpg",
      role: "Cost reference",
      reason:
        "Raw spread plus a separate commission shows the true price of execution, which is the only way to check the cost model on this site against a real statement.",
    },
    {
      name: "Exness",
      logo: "/ex.jpg",
      role: "Everyday use",
      reason:
        "Built for phone-first sessions, so pending orders, partial closes and withdrawal flow stay usable when you are not at a desk.",
    },
  ] as BrokerReason[],
  footnote:
    "Commission is paid on all three, so payout never decides the order. If a broker stops offering stop-limit or OCO on standard accounts, it comes off this list.",
};

export type BrokerRationaleKey =
  | "market-orders"
  | "limit-orders"
  | "stop-orders"
  | "stop-limit-orders"
  | "order-execution";

export interface BrokerRationale {
  title: string;
  lead: string;
  criteria: SelectionCriterion[];
  reasons: BrokerReason[];
  footnote: string;
}

/**
 * One rationale per page. The screening criteria change with the order type,
 * because the failure mode changes: market orders slip, limit orders go unfilled,
 * stop-limit orders do both. Reusing one generic paragraph everywhere would make
 * the recommendation look decorative.
 *
 * Card order is driven by `reasons` order — index 0 is the closest fit for that
 * page and renders first, left to right. Keep the two in sync instead of keeping
 * a separate ranking array: one source of truth means the strip and the expanded
 * note can never contradict each other.
 */
export const brokerRationales: Record<BrokerRationaleKey, BrokerRationale> = {
  "market-orders": {
    title: "Why these three",
    lead:
      "A market order is the only one on this site that can slip, so the screening here is about what happens after you click — not about whose platform looks best.",
    criteria: [
      {
        label: "Deviation control",
        text: "The ticket has to let you set a maximum deviation, so a fast market can be refused instead of filled far from the price you saw.",
      },
      {
        label: "Spread in the open",
        text: "Quotes have to stay usable through the London open and news releases — that is precisely when a market order costs the most.",
      },
      {
        label: "Fill versus quote",
        text: "Average fill price against the quoted price must appear on the statement, or there is no way to audit what the slippage actually cost.",
      },
    ],
    reasons: [
      {
        name: "XM",
        logo: "/xm.jpg",
        role: "See the slip",
        reason:
          "Micro sizing turns a 20-pip slip into cents, so the mechanic can be watched happening instead of taken on faith.",
      },
      {
        name: "IC Markets",
        logo: "/ic.jpg",
        role: "Measure the slip",
        reason:
          "Raw spread plus a separate commission keeps the cost of the spread apart from the cost of the fill, which is the whole point of this page.",
      },
      {
        name: "Exness",
        logo: "/ex.jpg",
        role: "Live sessions",
        reason:
          "One-click execution and a phone-first flow, for the sessions where a market order is actually placed in a hurry.",
      },
    ],
    footnote:
      "No broker can promise a market-order price — that is the nature of the order, not a failure of the broker. What differs is how much warning you get first.",
  },

  "limit-orders": {
    title: "Why these three",
    lead:
      "A limit order never slips. The risk is the opposite one: it never fills. So the screening is about whether your price truly rests in the market, or is quietly held by the dealer.",
    criteria: [
      {
        label: "Real book",
        text: "Limit orders need to rest in an actual order book rather than being warehoused at the dealer's own price.",
      },
      {
        label: "Inside the spread",
        text: "Some brokers reject limit prices between bid and ask, which shrinks where you are allowed to queue.",
      },
      {
        label: "Partial fills",
        text: "A partially filled limit has to be visible immediately, so you know how much is still working.",
      },
    ],
    reasons: [
      {
        name: "IC Markets",
        logo: "/ic.jpg",
        role: "Queue in the book",
        reason:
          "ECN-style pricing with depth of market, so it is visible where your limit order is actually sitting in the queue.",
      },
      {
        name: "XM",
        logo: "/xm.jpg",
        role: "Cheap resting size",
        reason:
          "Micro lots let a limit sit at one exact level without tying up meaningful capital while it waits.",
      },
      {
        name: "Exness",
        logo: "/ex.jpg",
        role: "Adjust while resting",
        reason:
          "Pending orders sit next to partial-close controls, which matters when a limit fills in pieces over a session.",
      },
    ],
    footnote:
      "No broker can guarantee a limit fill — that is the trade you accepted in exchange for the price. The honest test is how clearly the ticket tells you nothing happened yet.",
  },

  "stop-orders": {
    title: "Why these three",
    lead:
      "A stop order turns into a market order the instant it triggers, so the entire question is what happens in the second after the trigger — not how the order is placed.",
    criteria: [
      {
        label: "Trigger basis",
        text: "Whether the stop triggers on bid, ask or last price has to be stated up front, or the trigger level itself is guesswork.",
      },
      {
        label: "Trigger to fill",
        text: "The gap between trigger price and filled price must be readable on the statement, because that gap is the real cost of a stop.",
      },
      {
        label: "Guaranteed stops",
        text: "Where a guaranteed stop exists, its cost is the only genuine hedge against a weekend or news gap.",
      },
    ],
    reasons: [
      {
        name: "XM",
        logo: "/xm.jpg",
        role: "Fire it cheaply",
        reason:
          "Micro sizing makes a trigger cost a few dollars, which is the right price for watching a gap fire once before trusting the tool.",
      },
      {
        name: "IC Markets",
        logo: "/ic.jpg",
        role: "Widest toolset",
        reason:
          "The full order set on one ticket, including the variants most brokers drop, plus depth of market to see what the stop is firing into.",
      },
      {
        name: "Exness",
        logo: "/ex.jpg",
        role: "Overnight holds",
        reason:
          "Mobile alerts and quick funding suit stops left running across sessions — but confirm the gap policy for your entity before relying on it over a weekend.",
      },
    ],
    footnote:
      "A stop is a request, not a guarantee: the price can jump straight past your level. Anyone presenting a plain stop as protection against gaps is misdescribing it.",
  },

  "stop-limit-orders": {
    title: "Why these three",
    lead:
      "This is the only order here with two prices and two failure modes — it can trigger and still never fill. So the screening is about the ticket, not the pricing table.",
    criteria: [
      {
        label: "Both prices, one ticket",
        text: "Stop price and limit price must sit side by side. Brokers that bury stop-limit behind a phone call are out immediately.",
      },
      {
        label: "OCO and brackets",
        text: "Triggering one leg should cancel the other, otherwise the protection this order exists for collapses.",
      },
      {
        label: "Minimum distance",
        text: "The gap between stop and limit price cannot be set arbitrarily wide, or the order becomes useless at short range.",
      },
    ],
    reasons: [
      {
        name: "IC Markets",
        logo: "/ic.jpg",
        role: "Full ticket",
        reason:
          "Stop-limit and OCO on MT4, MT5 and cTrader, with depth of market to judge whether the limit side has any chance of filling.",
      },
      {
        name: "XM",
        logo: "/xm.jpg",
        role: "Learn the miss",
        reason:
          "Micro sizing means experiencing an unfilled stop-limit first-hand costs almost nothing — the one lesson this order type teaches.",
      },
      {
        name: "Exness",
        logo: "/ex.jpg",
        role: "Manage on mobile",
        reason:
          "Stop-limit plus partial close in the app, so the limit price can be adjusted after the trigger rather than abandoned.",
      },
    ],
    footnote:
      "The one thing no broker fixes: in a gap, a triggered stop-limit may never fill. That is the design, not a defect — which is why the limit price has to be chosen deliberately.",
  },

  "order-execution": {
    title: "Why these three",
    lead:
      "This page is about what execution costs, so the screening is about whether a broker lets you verify those costs — not about which one is best in some absolute sense.",
    criteria: [
      {
        label: "Costs separable",
        text: "Spread and commission have to be readable apart, rather than blended into a single number nobody can audit.",
      },
      {
        label: "Session liquidity",
        text: "Quotes must hold up through the rollover and the London open — the two windows where the cost model on this page is stress-tested.",
      },
      {
        label: "Statement detail",
        text: "Fill price, timestamp and venue on the statement. Without those three, none of the four costs above can be checked against reality.",
      },
    ],
    reasons: [
      {
        name: "IC Markets",
        logo: "/ic.jpg",
        role: "The reference case",
        reason:
          "Raw spread plus a stated commission is the cleanest way to put the cost model above against a real statement line by line.",
      },
      {
        name: "XM",
        logo: "/xm.jpg",
        role: "The simple baseline",
        reason:
          "A flat, wider spread with no commission shows what the convenient version of execution costs — useful as the control case.",
      },
      {
        name: "Exness",
        logo: "/ex.jpg",
        role: "The session test",
        reason:
          "Place the same order at the London open from a phone, then compare the fill against the latency timeline on this page.",
      },
    ],
    footnote:
      "Costs move with volatility and account tier. All three publish their pricing; none is cheapest in every session, and any page claiming otherwise is selling something.",
  },
};

/**
 * Partner / promotional broker links.
 * Swap `url` for your own affiliate tracking link when you have one — everything
 * else on the card keeps working. Every link opens in a new tab with
 * rel="sponsored nofollow noopener".
 */
export const brokerPartners: BrokerPartner[] = [
  {
    name: "XM",
    logo: "/xm.jpg",
    tagline: "The gentlest place to test these order types for the first time.",
    note: "Micro and standard accounts, tight minimum deposits and a very large education library — useful while you are still learning how a stop-limit behaves in a fast market.",
    url: "http://www.fxtrk.net/index.php?web=fxordertypes.com&jump=https://clicks.pipaffiliates.com/c?c=1333193&l=en&p=1",
    badge: "Best for beginners",
    specs: [
      { label: "Platforms", value: "MT4 / MT5 / XM app" },
      { label: "Account fit", value: "Micro & standard" },
      { label: "Order tools", value: "Stop, limit, stop-limit, OCO" },
    ],
  },
  {
    name: "IC Markets",
    logo: "/ic.jpg",
    tagline: "Raw-spread pricing if you care what execution actually costs.",
    note: "Built around ECN-style pricing with a separate commission, so the spread you see is closer to the interbank one. Good match for the execution and slippage material on this site.",
    url: "http://www.fxtrk.net/index.php?web=fxordertypes.com&jump=https://www.icmarkets.com/global/en/open-trading-account/live/?camp=6849",
    badge: "Best for raw spreads",
    specs: [
      { label: "Platforms", value: "MT4 / MT5 / cTrader" },
      { label: "Account fit", value: "Raw spread & standard" },
      { label: "Order tools", value: "All order types, depth of market" },
    ],
  },
  {
    name: "Exness",
    logo: "/ex.jpg",
    tagline: "Fast funding and a clean mobile flow, with instant withdrawals.",
    note: "Widely used outside the EU, with a simplified account ladder and quick withdrawal handling. Convenient if you mostly trade from a phone during a session.",
    url: "http://www.fxtrk.net/index.php?web=fxordertypes.com&jump=https://one.exnessonelink.com/boarding/sign-up/a/16004220",
    badge: "Best for mobile",
    specs: [
      { label: "Platforms", value: "MT4 / MT5 / Exness app" },
      { label: "Account fit", value: "Standard & pro" },
      { label: "Order tools", value: "Pending orders, partial close" },
    ],
  },
];

export const partnerDisclosure =
  "Some links above are partner links. If you open an account through one, this site may receive a commission at no extra cost to you. That never changes what we write — order mechanics are the same at every broker. Broker terms, regulation and leverage limits differ by country and can change; always confirm them on the broker's own site before funding an account.";